/**
 * Cut-Corner (Angled-Corner) Cushion Configurator — UNIVERSAL-BLOCK version
 * uscushion.com · 2026-08
 *
 * Runs INSIDE the shared cushion-configurator-block.liquid (formula_type == 'cutcorner').
 * Same integration pattern as cushion-configurator-baywindow.js:
 *   - adapts the shared #cc-configurator DOM via transformDOM()
 *   - reads the shared fabric selector (window.__ucfs_selectedFabric / fabric:selected)
 *   - honours window.__CC_COVER_FACTOR for "<shape>_cover" products
 *
 * Shape: symmetric 6-sided cushion — a rectangle with the two TOP corners cut off.
 *   Customer inputs (inches), mapped onto the shared dimension slots:
 *     A = bottom edge width (widest)   -> #cc-dim-l
 *     D = top edge width (narrower)     -> #cc-dim-w
 *     T = thickness                     -> #cc-dim-t
 *     B = side height (straight run)    -> #cc-back-l
 *     C = slanted corner length         -> #cc-back-w
 *   Derived: inset=(A-D)/2, slant rise=sqrt(C^2-inset^2), total height=rise+B.
 *   Zipper runs along the top edge (D) AND both slanted corners (C).
 *   Pricing identical to the diamond/standalone engine (material on bounding box,
 *   shipping weight on the true polygon area).
 */
(function () {
  'use strict';

  /* ─── Cost constants ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) — mirrors the shared block's controls ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  /* Throw-pillow physical sizes — folded into shipping weight (matches diamond engine) */
  var PILLOW_DIMS = {
    none:         { l: 0,  w: 0,  q: 0 },
    '2pcs_12x20': { l: 12, w: 20, q: 2 }, '2pcs_16x16': { l: 16, w: 16, q: 2 },
    '2pcs_18x18': { l: 18, w: 18, q: 2 }, '2pcs_20x20': { l: 20, w: 20, q: 2 },
    '2pcs_24x24': { l: 24, w: 24, q: 2 }, '2pcs_custom': { l: 20, w: 20, q: 2 },
    '4pcs_12x20': { l: 12, w: 20, q: 4 }, '4pcs_16x16': { l: 16, w: 16, q: 4 },
    '4pcs_18x18': { l: 18, w: 18, q: 4 }, '4pcs_20x20': { l: 20, w: 20, q: 4 },
    '4pcs_24x24': { l: 24, w: 24, q: 4 }, '4pcs_custom': { l: 20, w: 20, q: 4 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  var DEF = { a: 70, b: 12, c: 18, d: 40, t: 3 };

  /* ─── State ─── */
  var state = {
    a: 0, b: 0, c: 0, d: 0, t: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Geometry ─── */
  function inset(a, d)  { return (a - d) / 2; }
  function slantRise(a, c, d) {
    var ins = inset(a, d);
    var r = c * c - ins * ins;
    return r > 0 ? Math.sqrt(r) : 0;
  }
  function totalHeight(a, b, c, d) { return slantRise(a, c, d) + b; }
  function vertices(a, b, c, d) {
    var rise = slantRise(a, c, d);
    return [
      [-d / 2, 0],
      [ d / 2, 0],
      [ a / 2, rise],
      [ a / 2, rise + b],
      [-a / 2, rise + b],
      [-a / 2, rise]
    ];
  }
  function polygonAreaIn2(v) {
    var a = 0;
    for (var i = 0; i < v.length; i++) { var j = (i + 1) % v.length; a += v[i][0] * v[j][1] - v[j][0] * v[i][1]; }
    return Math.abs(a) / 2;
  }
  function perimeterIn(v) {
    var p = 0;
    for (var i = 0; i < v.length; i++) { var j = (i + 1) % v.length; p += Math.hypot(v[j][0] - v[i][0], v[j][1] - v[i][1]); }
    return p;
  }
  function bboxAreaIn2(v) {
    var xs = v.map(function (p) { return p[0]; }), ys = v.map(function (p) { return p[1]; });
    return (Math.max.apply(null, xs) - Math.min.apply(null, xs)) * (Math.max.apply(null, ys) - Math.min.apply(null, ys));
  }

  /* ─── Pricing (bench parity: bbox material + weight incl. fabric; ×cover factor) ─── */
  function calcBasePrice(v, T, ec, pk) {
    if (!v || !T) return null;
    var IN = 39.37;
    var actualAreaM2 = polygonAreaIn2(v) / (IN * IN);
    var matAreaM2    = bboxAreaIn2(v) / (IN * IN);
    var perimM       = perimeterIn(v) / IN;
    var Tm           = T / IN;
    var fabricAreaMat = matAreaM2 * 2 + perimM * Tm;
    var foamVolMat    = matAreaM2 * Tm;
    var totalWeight = foamVolMat * FOAM_DENSITY + fabricAreaMat * FABRIC_GSM / 1000;
    if (pk && pk !== 'none') {
      var p = PILLOW_DIMS[pk];
      if (p) totalWeight += (p.l / IN) * (p.w / IN) * 2 * FABRIC_GSM / 1000 * p.q;
    }
    var totalRMB = fabricAreaMat * fabricRate() + foamVolMat * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    var base = totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio();
    return Math.max(base * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam). Mirrors calcBasePrice's fabric
     basis: MATERIAL uses the bounding rect (bbox), shipping weight uses the finished
     (actual) area. Fabric grade auto via fabricRate, at COVER_COST_RATIO (45%). */
  function extraCoverUnit() {
    if (!dimsValid()) return 0;
    var IN = 39.37;
    var v = vertices(state.a, state.b, state.c, state.d);
    var actualAreaM2 = polygonAreaIn2(v) / (IN * IN);
    var matAreaM2 = bboxAreaIn2(v) / (IN * IN);
    var perimM = perimeterIn(v) / IN, Tm = state.t / IN;
    var matFabric = matAreaM2 * 2 + perimM * Tm;                        // material: bounding rect
    var shipWeight = (actualAreaM2 * 2 + perimM * Tm) * FABRIC_GSM / 1000; // ship: finished cover
    var totalRMB = matFabric * fabricRate() + shipWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'No extra cover'; continue; }
      var label = (n === 1 ? '1 set' : n + ' sets');
      opts[i].text = unit > 0 ? label + ' (+$' + (unit * n) + ')' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function dimsValid() {
    return state.a >= 6 && state.d >= 2 && state.a >= state.d
      && state.b >= 1 && state.t >= 1
      && state.c > inset(state.a, state.d) + 0.05;
  }
  function isComplete() {
    return dimsValid() && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════ SVG preview (flat top view) ═══════════════════════ */
  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;
    svg.setAttribute('viewBox', '0 0 460 300');

    var empty = !(state.a || state.b || state.c || state.d);
    var a = empty ? DEF.a : state.a || DEF.a;
    var b = empty ? DEF.b : state.b || DEF.b;
    var c = empty ? DEF.c : state.c || DEF.c;
    var d = empty ? DEF.d : state.d || DEF.d;
    var ins = inset(a, d);
    if (c <= ins) c = Math.hypot(ins, Math.max(6, b * 0.6));

    var v = vertices(a, b, c, d);
    var shapeH = slantRise(a, c, d) + b;

    var VB_W = 460, VB_H = 300;
    var padX = 92, padY = 50;
    var shapeW = a;
    var availW = VB_W - padX * 2, availH = VB_H - padY * 2;
    var scale = Math.min(availW / shapeW, availH / shapeH);
    var offX = VB_W / 2, offY = padY;
    function P(pt) { return [offX + pt[0] * scale, offY + pt[1] * scale]; }
    function s(pp) { return pp[0].toFixed(1) + ',' + pp[1].toFixed(1); }
    function f(n) { return n.toFixed(1); }

    var sp = v.map(P);

    var fillC = '#EDE9E0', strokeC = '#2A2A28';
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        fillC = lighten(baseHex, 0.45);
        strokeC = darken(baseHex, 0.45);
        var scc = hexToRgb(strokeC);
        if ((scc[0] + scc[1] + scc[2]) / 3 > 150) strokeC = '#2A2A28';
      }
    }

    var GREEN = '#5a9e2a', DIMC = '#1a4f78';
    var h = '';
    h += '<defs><marker id="xc-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';
    h += '<polygon points="' + sp.map(s).join(' ') + '" fill="' + fillC + '" stroke="' + strokeC + '" stroke-width="1.6" stroke-linejoin="round"/>';

    function edge(i, j, color, w) {
      h += '<line x1="' + f(sp[i][0]) + '" y1="' + f(sp[i][1]) + '" x2="' + f(sp[j][0]) + '" y2="' + f(sp[j][1])
        + '" stroke="' + color + '" stroke-width="' + w + '" stroke-linecap="round"/>';
    }
    edge(0, 1, GREEN, 3.4);  // top edge D  = zipper
    edge(1, 2, GREEN, 3.2);  // right slant C = zipper
    edge(5, 0, GREEN, 3.2);  // left slant  C = zipper

    if (state.piping === 'single' || state.piping === 'double') {
      h += '<polygon points="' + sp.map(s).join(' ') + '" fill="none" stroke="#F5F1E8" stroke-width="1.6" stroke-linejoin="round" opacity="0.8"/>';
    }

    if (!empty) {
      function lbl(x, y, txt, color, anchor, size) {
        h += '<text x="' + f(x) + '" y="' + f(y) + '" text-anchor="' + (anchor || 'middle')
          + '" font-size="' + (size || 10.5) + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + color + '">' + txt + '</text>';
      }
      function measure(pa, pb, off, color) {
        var dx = pb[0] - pa[0], dy = pb[1] - pa[1], L = Math.hypot(dx, dy) || 1;
        var nx = -dy / L, ny = dx / L;
        var a2 = [pa[0] + nx * off, pa[1] + ny * off], b2 = [pb[0] + nx * off, pb[1] + ny * off];
        h += '<line x1="' + f(pa[0]) + '" y1="' + f(pa[1]) + '" x2="' + f(a2[0]) + '" y2="' + f(a2[1]) + '" stroke="' + color + '" stroke-width="0.5"/>';
        h += '<line x1="' + f(pb[0]) + '" y1="' + f(pb[1]) + '" x2="' + f(b2[0]) + '" y2="' + f(b2[1]) + '" stroke="' + color + '" stroke-width="0.5"/>';
        h += '<line x1="' + f(a2[0]) + '" y1="' + f(a2[1]) + '" x2="' + f(b2[0]) + '" y2="' + f(b2[1]) + '" stroke="' + color + '" stroke-width="1.1" marker-start="url(#xc-arr)" marker-end="url(#xc-arr)"/>';
        return [(a2[0] + b2[0]) / 2, (a2[1] + b2[1]) / 2];
      }
      var mD = measure(sp[0], sp[1], -22, DIMC); lbl(mD[0], mD[1] - 5, 'D ' + d + '″', DIMC);
      var mA = measure(sp[4], sp[3], 22, DIMC); lbl(mA[0], mA[1] + 12, 'A ' + a + '″', DIMC);
      var rx = Math.max(sp[2][0], sp[3][0]) + 30;
      h += '<line x1="' + f(rx) + '" y1="' + f(sp[2][1]) + '" x2="' + f(rx) + '" y2="' + f(sp[3][1]) + '" stroke="' + DIMC + '" stroke-width="1.1" marker-start="url(#xc-arr)" marker-end="url(#xc-arr)"/>';
      lbl(rx + 4, (sp[2][1] + sp[3][1]) / 2 + 3, 'B ' + b + '″', DIMC, 'start');
      var cVal = (state.c || c);
      lbl((sp[1][0] + sp[2][0]) / 2 + 12, (sp[1][1] + sp[2][1]) / 2, 'C ' + cVal + '″', GREEN, 'start', 9.5);
      lbl((sp[5][0] + sp[0][0]) / 2 - 12, (sp[5][1] + sp[0][1]) / 2, 'C ' + cVal + '″', GREEN, 'end', 9.5);
      lbl(offX, offY - 42, '▲ Green edges (top + corners) = zipper', GREEN, 'middle', 9);
    } else {
      h += '<text x="230" y="288" text-anchor="middle" font-size="10.5" font-family="Poppins,sans-serif" fill="#8A8A85">Enter dimensions below to see your shape</text>';
    }
    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (dimsValid()) parts.push('Cut-corner A' + state.a + ' B' + state.b + ' C' + state.c + ' D' + state.d + ' × ' + state.t + '″');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') parts.push(LABELS[state.piping]);
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your cut-corner cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;

    state.a = parseFloat(els.inA.value) || 0;
    state.d = parseFloat(els.inD.value) || 0;
    state.t = parseFloat(els.inT.value) || 0;
    state.b = els.inB ? (parseFloat(els.inB.value) || 0) : 0;
    state.c = els.inC ? (parseFloat(els.inC.value) || 0) : 0;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) { state.pipingColor = 'match'; if (els.pipingColor) els.pipingColor.value = 'match'; }
    }

    [['inA', state.a], ['inB', state.b], ['inC', state.c], ['inD', state.d], ['inT', state.t]].forEach(function (p) {
      if (els[p[0]]) els[p[0]].classList.toggle('ok', p[1] > 0);
    });

    drawCushion();
    updateSummary();

    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var v = dimsValid() ? vertices(state.a, state.b, state.c, state.d) : null;
    var base = v ? calcBasePrice(v, state.t, ec, state.throwPillow) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      els.priceMain.textContent = '$' + Math.round(base + addonTotal);
      var parts = ['Base $' + Math.round(base)];
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ec > 0)                         parts.push('Covers +$' + (extraCoverUnit() * ec));
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('P.Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    [els.addBtn, els.addBtnSticky].filter(Boolean).forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        btn.textContent = 'Add to order — $' + Math.round(base + addonTotal) + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!dimsValid()) missing.push('all dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: base !== null ? Math.round(base + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var v = vertices(state.a, state.b, state.c, state.d);
    var base = calcBasePrice(v, state.t, ec, state.throwPillow);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Shape': 'Cut-corner (angled top corners)',
      'A — Bottom edge': state.a + '"',
      'B — Side height': state.b + '"',
      'C — Slant corner': state.c + '"',
      'D — Top edge': state.d + '"',
      'Thickness': state.t + '"',
      'Total height': totalHeight(state.a, state.b, state.c, state.d).toFixed(1) + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.zipperPlacement) {
      props['Zipper Placement'] = state.zipperPlacement;
    } else {
      props['Zipper Placement'] = 'Top edge + both corners (C + D)';
    }
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Fabric Grade'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s) (+$' + (extraCoverUnit() * ec) + ')';
    if (state.throwPillow && state.throwPillow !== 'none')
      props['Throw Pillow'] = state.throwPillow.replace(/_/g, ' ') + ' (+$' + ADDON.throwPillow[state.throwPillow] + ')';
    if (state.pillowCovers && state.pillowCovers !== 'none')
      props['Pillow Covers'] = state.pillowCovers.replace(/_/g, ' ') + ' (+$' + ADDON.pillowCovers[state.pillowCovers] + ')';
    if (state.armrestPillow && state.armrestPillow !== 'none')
      props['Armrest Pillow'] = state.armrestPillow.replace(/_/g, ' ') + ' (+$' + ADDON.armrestPillow[state.armrestPillow] + ')';
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title;
    return { properties: props, price: finalPrice };
  }

  /* ─── DOM transformation (adapt shared Liquid section for Cut-Corner) ─── */
  function transformDOM() {
    // Fixed shape — hide the Shape picker (step 1)
    var shapeSection = document.getElementById('cc-shape-section');
    if (shapeSection) shapeSection.style.display = 'none';

    // Seat row → A (bottom) / D (top) / T (thickness)
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'A — Bottom edge (in) <span class="cc-req">*</span>';
    var inL = document.getElementById('cc-dim-l');
    if (inL) { inL.placeholder = 'e.g. 70'; inL.min = '6'; inL.max = '240'; }

    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'D — Top edge (in) <span class="cc-req">*</span>';
    var inW = document.getElementById('cc-dim-w');
    if (inW) { inW.placeholder = 'e.g. 40'; inW.min = '2'; inW.max = '240'; }

    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'T — Thickness (in) <span class="cc-req">*</span>';

    // Show the "back" row and repurpose it → B (side height) / C (slant corner)
    var backRows = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backRows.length; i++) {
      var el = backRows[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.textContent = 'Corners';

    var labelBL = document.querySelector('label[for="cc-back-l"]');
    if (labelBL) labelBL.innerHTML = 'B — Side height (in) <span class="cc-req">*</span>';
    var inBL = document.getElementById('cc-back-l');
    if (inBL) { inBL.placeholder = 'e.g. 12'; inBL.min = '1'; inBL.max = '160'; }

    var labelBW = document.querySelector('label[for="cc-back-w"]');
    if (labelBW) labelBW.innerHTML = 'C — Slant corner (in) <span class="cc-req">*</span>';
    var inBW = document.getElementById('cc-back-w');
    if (inBW) { inBW.placeholder = 'e.g. 18'; inBW.min = '2'; inBW.max = '160'; }

    // Cut-corner needs only 5 dims — hide the back thickness field
    var backT = document.getElementById('cc-back-t');
    if (backT && backT.closest('.cc-field')) backT.closest('.cc-field').style.display = 'none';

    // Update the hint under the preview
    var hint = document.querySelector('.cc-sp-hint');
    if (hint) hint.textContent = 'A = wide bottom, B = straight side, C = slanted corner, D = narrow top. Total height auto-calculated. Zipper runs along the top + both corners (C + D).';

    // Renumber steps since Shape (step 1) is hidden
    var sns = document.querySelectorAll('.cc-sec-head .cc-sn');
    var n = 1;
    for (var k = 0; k < sns.length; k++) {
      var sec = sns[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none' && sns[k].textContent.trim() !== 'i') {
        sns[k].textContent = n++;
      }
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;
    if ((wrap.dataset.formulaType || '') !== 'cutcorner') return; // safety: only run for cutcorner products

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.inA = document.getElementById('cc-dim-l');
    els.inD = document.getElementById('cc-dim-w');
    els.inT = document.getElementById('cc-dim-t');
    els.inB = document.getElementById('cc-back-l');
    els.inC = document.getElementById('cc-back-w');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['inA', 'inD', 'inT', 'inB', 'inC'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) { state.fabric = e.detail; recalc(); });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) { state.fabric = existingFabric; }
    setInterval(function () {
      var fb = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fb && fb !== state.fabric) { state.fabric = fb; recalc(); }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.CutCornerCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();